import { useState } from 'react'
import { api } from '../api/client'
import type { AnalysisResult } from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner, QualityBanner } from '../components/Banner'
import { Findings } from '../components/Findings'
import { StatTile } from '../components/StatTile'
import { TrendChart } from '../components/TrendChart'
import { WindowPicker } from '../components/WindowPicker'
import { formatRange } from '../format'

interface Props {
  end: string
  days: number
  maxDate: string
  propertyId?: string
  onWindowChange: (next: { end?: string; days?: number }) => void
}

/** Counts for one named GA4 event, broken down by channel. */
export function EventLookup({ end, days, maxDate, propertyId, onWindowChange }: Props) {
  const [draft, setDraft] = useState('')
  const [submitted, setSubmitted] = useState('')

  const { data, error, loading, validating } = useAsync<AnalysisResult>(
    (signal) => api.eventReport(submitted, { end, days, propertyId }, signal),
    [submitted, end, days, propertyId],
    submitted !== '',
    `event:${propertyId ?? ''}:${submitted}:${end}:${days}`,
  )

  return (
    <>
      <form
        className="controls"
        onSubmit={(event) => {
          event.preventDefault()
          setSubmitted(draft.trim())
        }}
      >
        <label className="field">
          <span className="field__label">Event name</span>
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="paywall_view"
            maxLength={100}
            required
            spellCheck={false}
          />
        </label>
        <button type="submit" className="button" disabled={validating || draft.trim() === ''}>
          {validating ? 'Analysing…' : 'Analyse'}
        </button>
      </form>

      <WindowPicker
        end={end}
        days={days}
        maxDate={maxDate}
        onChange={onWindowChange}
        disabled={loading || submitted === ''}
      />

      {submitted === '' && (
        <div className="card">
          <p className="placeholder">
            Enter a GA4 event name to see its counts by channel. The name is matched exactly.
          </p>
        </div>
      )}

      {error && <Banner tone="critical">{error}</Banner>}

      {data && !loading && (
        <>
          <QualityBanner quality={data.quality} />

          <p className="section__note" style={{ marginTop: 0 }}>
            <strong style={{ color: 'var(--text-primary)' }}>{submitted}</strong> ·{' '}
            {formatRange(data.profile.window.current_start, data.profile.window.current_end)} · compared
            with {formatRange(data.profile.window.comparison_start, data.profile.window.comparison_end)}
          </p>

          <div className="tiles" style={{ marginTop: 14 }}>
            {data.kpis.map((kpi) => (
              <StatTile key={kpi.name} kpi={kpi} comparisonLabel="previous period" hero />
            ))}
          </div>

          <section className="section">
            {data.series.map((series) => (
              <TrendChart key={series.metric} series={series} comparisonLabel="Previous period" />
            ))}
          </section>

          <Findings
            findings={data.findings}
            minimumSegmentUsers={data.profile.minimum_segment_users}
            minimumSegmentSharePercent={data.profile.minimum_segment_share_percent}
            thresholdPercent={data.profile.anomaly_threshold_percent}
            findingsMetric={submitted}
          />
        </>
      )}
    </>
  )
}
